/**
 * Tâche 1207 — Carte « Sentinelle Clés IA » (Eric 2026-06-26).
 *
 * Eric : « Il y a déjà deux pages qui permettent de suivre la santé des clés
 * IA : Configuration BYOK et Sentinelles ; utilise-les pour me prévenir. »
 *
 * Cette carte vit donc DANS la page Sentinelles, à côté de la sentinelle de
 * connexion, et suit les mêmes conventions : verdict global, fraîcheur
 * (dead-man's switch), détail des échecs, bouton « Tester maintenant ».
 *
 * Le bogue 1203 a montré le trou qu'elle comble : une clé refusée ne se
 * découvrait que lorsque Vincent signalait que les objections ne se
 * généraient plus.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowClockwise, CaretDown, CaretUp, Key } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtAgo = (seconds) => {
  if (seconds == null) return "jamais";
  if (seconds < 60) return `il y a ${Math.round(seconds)} s`;
  if (seconds < 3600) return `il y a ${Math.floor(seconds / 60)} min`;
  return `il y a ${Math.floor(seconds / 3600)} h`;
};

export const AIKeysSentinelCard = ({ initial, initialWeekly , awaiting }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  // Tâche 1211 (Eric 2026-06-26) — Le récap de 7 jours. La carte ne montrait
  // que l'instant présent : une clé qui a lâché mardi et qui répond de nouveau
  // redevenait invisible, et une clé qui ralentit lentement ne se voyait
  // jamais. Chargé en parallèle, et jamais bloquant pour la carte.
  const [weekly, setWeekly] = useState(null);
  const [weeklyOpen, setWeeklyOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/sentinel/ai-keys/status?limit=20`, {
        withCredentials: true,
      });
      setData(r.data);
    } catch (e) {
      // La carte ne doit jamais casser la page : on reste silencieux au
      // chargement automatique (l'erreur est visible dans l'état vide).
      setData(null);
    } finally {
      setLoading(false);
    }
    try {
      const w = await axios.get(`${API}/api/sentinel/ai-keys/weekly?days=7`, {
        withCredentials: true,
      });
      setWeekly(w.data);
    } catch (e) {
      setWeekly(null);
    }
  }, []);

    // Tâche 1249 — Si la page a déjà tout reçu dans l'appel groupé, la carte
  // ne refait PAS son propre appel : la page passait 23 appels par affichage.
  useEffect(() => {
    if (initial) {
      setData(initial);
      if (initialWeekly) setWeekly(initialWeekly);
      return;
    }
    // Tâche 1249 — L'appel groupé est en route : ne pas en lancer un second.
    if (awaiting) return;
    load();
  }, [load, initial, initialWeekly, awaiting]);

  const testNow = async () => {
    setTesting(true);
    try {
      const r = await axios.post(`${API}/api/sentinel/ai-keys/run-now`, {}, {
        withCredentials: true,
      });
      const failing = r.data?.failing_total ?? 0;
      if (failing > 0) toast.error(`${failing} clé(s) refusée(s) — détail ci-dessous`);
      else toast.success("Toutes les clés surveillées répondent");
      await load();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally {
      setTesting(false);
    }
  };

  const failing = data?.failing || [];
  const state = loading
    ? "loading"
    : !data
      ? "unknown"
      : data.stale
        ? "stale"
        : failing.length > 0
          ? "red"
          : "green";
  const box = {
    loading: "bg-zinc-50 text-zinc-700 border-zinc-200",
    unknown: "bg-zinc-100 text-zinc-800 border-zinc-300",
    stale: "bg-zinc-100 text-zinc-800 border-zinc-300",
    green: "bg-emerald-50 text-emerald-900 border-emerald-300",
    red: "bg-red-50 text-red-900 border-red-400",
  }[state];
  const label = {
    loading: "Chargement…",
    unknown: "État inconnu",
    stale: "État inconnu — sentinelle figée",
    green: "Toutes les clés IA surveillées répondent",
    red: `${failing.length} clé(s) IA refusée(s)`,
  }[state];
  const dot = {
    loading: "bg-zinc-300",
    unknown: "bg-zinc-400",
    stale: "bg-zinc-400",
    green: "bg-emerald-500",
    red: "bg-red-500",
  }[state];

  const intervalMin = data?.interval_seconds ? Math.round(data.interval_seconds / 60) : 30;

  return (
    <section
      className="rounded-xl border border-zinc-200 bg-white p-5"
      data-testid="ai-keys-sentinel-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-semibold text-zinc-900 flex items-center gap-2">
            <Key size={18} weight="duotone" /> Sentinelle Clés IA
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5 max-w-3xl">
            Chaque clé configurée (OpenAI, AssemblyAI, Resend) est testée par un vrai
            mini-appel toutes les {intervalMin} min, pour chaque organisation. Une clé
            refusée ouvre un incident : courriel immédiat, rappels, et journal ci-dessous.
            Les résultats alimentent aussi la page <strong>Forfait AI</strong>.
            Une clé non configurée n'est pas surveillée.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={testNow}
            disabled={testing}
            className="px-3 py-2 text-sm rounded-lg bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50 transition-colors"
            data-testid="ai-keys-test-now-btn"
          >
            {testing ? "Test en cours…" : "Tester les clés maintenant"}
          </button>
          <button
            onClick={load}
            className="px-3 py-2 text-sm rounded-lg border border-zinc-300 hover:bg-zinc-50 transition-colors"
            data-testid="ai-keys-refresh-btn"
            aria-label="Rafraîchir"
          >
            <ArrowClockwise size={16} />
          </button>
        </div>
      </div>

      <div className={`mt-4 rounded-lg border p-4 ${box}`} data-testid="ai-keys-sentinel-overall">
        <div className="flex items-center gap-3 flex-wrap">
          <span className={`w-3 h-3 rounded-full ${dot}`} aria-hidden />
          <span className="font-semibold" data-testid="ai-keys-sentinel-verdict">{label}</span>
          <span className="text-xs opacity-80" data-testid="ai-keys-sentinel-freshness">
            dernier passage {fmtAgo(data?.age_seconds)}
            {data?.state?.monitored_total != null
              ? ` · ${data.state.monitored_total} clé(s) surveillée(s)`
              : ""}
          </span>
        </div>
        {state === "stale" && (
          <p className="text-sm mt-2" data-testid="ai-keys-sentinel-stale-warning">
            Aucun passage récent : la sentinelle est peut-être bloquée. Les résultats
            affichés peuvent être périmés.
          </p>
        )}
      </div>

      {failing.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="ai-keys-failing-table">
            <thead>
              <tr className="text-left text-zinc-500 border-b border-zinc-200">
                <th className="py-2 pr-3 font-medium">Organisation</th>
                <th className="py-2 pr-3 font-medium">Service</th>
                <th className="py-2 pr-3 font-medium">Clé</th>
                <th className="py-2 font-medium">Diagnostic</th>
              </tr>
            </thead>
            <tbody>
              {failing.map((f) => (
                <tr
                  key={`${f.org_id}-${f.service}`}
                  className="border-b border-zinc-100 last:border-0"
                  data-testid={`ai-keys-failing-row-${f.service}`}
                >
                  <td className="py-2 pr-3">{f.org_name || f.org_id}</td>
                  <td className="py-2 pr-3 font-medium">{f.service}</td>
                  <td className="py-2 pr-3 font-mono text-xs">
                    {f.key_last4 ? `••••${f.key_last4}` : "—"}
                  </td>
                  <td className="py-2 text-rose-700">{f.message || f.error_type || "Refusée"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Tâche 1211 (Eric 2026-06-26) — Récap des 7 derniers jours : latence
          moyenne par service, taux de réussite, et les incidents de la période
          MÊME s'ils sont refermés. C'est la tendance qui prévient une panne. */}
      {weekly?.services?.length > 0 && (
        <div className="mt-4 rounded-lg border border-zinc-200" data-testid="ai-keys-weekly-block">
          <button
            onClick={() => setWeeklyOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-zinc-50 transition-colors"
            data-testid="ai-keys-weekly-toggle"
            aria-expanded={weeklyOpen}
          >
            <span className="text-sm font-semibold text-zinc-800">
              Récap des {weekly.days} derniers jours
              <span className="ml-2 text-xs font-normal text-zinc-500">
                {weekly.runs} passage(s) · {weekly.incidents_total} incident(s)
              </span>
            </span>
            {weeklyOpen ? <CaretUp size={14} /> : <CaretDown size={14} />}
          </button>
          {weeklyOpen && (
            <div className="px-3 pb-3">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="ai-keys-weekly-table">
                  <thead>
                    <tr className="text-left text-zinc-500 border-b border-zinc-200">
                      <th className="py-2 pr-3 font-medium">Service</th>
                      <th className="py-2 pr-3 font-medium">Réussite</th>
                      <th className="py-2 pr-3 font-medium whitespace-nowrap">Latence moy.</th>
                      <th className="py-2 font-medium">Échecs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weekly.services.map((s) => (
                      <tr
                        key={s.service}
                        className="border-b border-zinc-100 last:border-0"
                        data-testid={`ai-keys-weekly-row-${s.service}`}
                      >
                        <td className="py-2 pr-3 font-medium">{s.service}</td>
                        <td className={`py-2 pr-3 ${s.failures ? "text-rose-700" : "text-emerald-700"}`}>
                          {s.success_pct} %
                        </td>
                        <td className="py-2 pr-3 tabular-nums">
                          {s.avg_latency_ms != null ? `${s.avg_latency_ms} ms` : "—"}
                        </td>
                        <td className="py-2">
                          {s.failures}/{s.checks}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {weekly.incidents?.length > 0 ? (
                <ul className="mt-3 space-y-1" data-testid="ai-keys-weekly-incidents">
                  {weekly.incidents.slice(0, 10).map((i) => (
                    <li key={i.key + (i.opened_at || "")} className="text-xs text-zinc-700">
                      <strong>{i.title || i.key}</strong>
                      {" — ouvert le "}
                      {String(i.opened_at || "").slice(0, 10)}
                      {i.resolved_at
                        ? `, refermé le ${String(i.resolved_at).slice(0, 10)}`
                        : " · ENCORE OUVERT"}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 text-xs text-emerald-700" data-testid="ai-keys-weekly-no-incident">
                  Aucun incident de clé IA sur la période.
                </p>
              )}
              <p className="mt-2 text-[11px] text-zinc-500">
                Ce même récap est joint au rapport de fiabilité du lundi matin.
              </p>
            </div>
          )}
        </div>
      )}

      {data?.history?.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="ai-keys-history-table">
            <thead>
              <tr className="text-left text-zinc-500 border-b border-zinc-200">
                <th className="py-2 pr-3 font-medium">Passage</th>
                <th className="py-2 pr-3 font-medium hidden sm:table-cell">Déclencheur</th>
                <th className="py-2 pr-3 font-medium">Surveillées</th>
                {/* « Échecs » et non « En échec » : sur 390 px l'en-tête plus
                    long était rogné par la largeur de colonne. */}
                <th className="py-2 font-medium whitespace-nowrap">Échecs</th>
              </tr>
            </thead>
            <tbody>
              {data.history.slice(0, 10).map((h) => (
                <tr key={h.id} className="border-b border-zinc-100 last:border-0">
                  <td className="py-2 pr-3 text-xs">
                    {h.started_at
                      ? new Date(h.started_at).toLocaleString("fr-CA", {
                          dateStyle: "short",
                          timeStyle: "short",
                        })
                      : "—"}
                  </td>
                  <td className="py-2 pr-3 text-xs hidden sm:table-cell">{h.trigger}</td>
                  <td className="py-2 pr-3">{h.monitored_total ?? "—"}</td>
                  <td className={`py-2 font-medium ${h.failing_total ? "text-rose-700" : "text-emerald-700"}`}>
                    {h.failing_total ?? 0}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default AIKeysSentinelCard;
